import { BREAKPOINTS, type Breakpoint } from '../hooks/useBreakpoint';

/**
 * The smallest viewport at which a column earns its place.
 * `always` means it is never dropped, however narrow the screen gets.
 */
export type ColumnPriority = 'always' | Breakpoint;

/** The subset of a column definition this module needs. */
export interface PrioritisedColumn {
  id?: string;
  accessorKey?: string | number | symbol;
  meta?: {
    priority?: ColumnPriority;
    incidental?: boolean;
  };
}

/** Column id, matching how TanStack derives it when `id` is omitted. */
export function columnId(column: PrioritisedColumn): string | undefined {
  if (column.id) return column.id;
  return typeof column.accessorKey === 'string' ? column.accessorKey : undefined;
}

/**
 * Which columns should be visible at `width`.
 *
 * Kept as a pure function, separate from the component, because it is the rule
 * that decides what a track operator can see on a tablet at the start line —
 * and it is far easier to prove correct here than to eyeball across five
 * viewport sizes.
 */
export function resolveColumnVisibility(
  columns: PrioritisedColumn[],
  width: number
): Record<string, boolean> {
  const model: Record<string, boolean> = {};
  for (const column of columns) {
    const id = columnId(column);
    if (!id) continue;
    const priority = column.meta?.priority ?? 'always';
    model[id] = priority === 'always' || width >= BREAKPOINTS[priority];
  }
  return model;
}

/**
 * How many meaningful columns are hidden at `width`.
 *
 * Columns marked `incidental` (icon and action columns) are excluded — telling
 * someone "1 column hidden" when the hidden column is a blank icon slot is
 * noise, not information.
 */
export function countHiddenColumns(columns: PrioritisedColumn[], width: number): number {
  const visibility = resolveColumnVisibility(columns, width);
  return columns.filter((column) => {
    const id = columnId(column);
    return id !== undefined && visibility[id] === false && !column.meta?.incidental;
  }).length;
}
